<template>
  <div class="wrapper">
    <div class="container">
      <div v-if="!order" class="account-pages mt-5 mb-5">
        <div class="row justify-content-center">
          <div class="col-lg-5">
            <div class="card">
              <!-- Logo -->
              <div class="card-header pt-5 text-center">
                <base-logo item-class="logo-lg" :height="80" />
              </div>

              <div class="card-body p-4">
                <form @submit.prevent="getOrder">
                  <div>
                    <base-input
                      type="text"
                      :label="$t('views.pages.identification_number')"
                      :placeholder="$t('views.pages.enter_your_id_number')"
                      v-model="tckn"
                      :class="status(v$.tckn)"
                      @blur="v$.tckn.$touch()"
                      tabindex="1"
                      :autofocus="true"
                      @focus="v$.tckn.$reset()"
                      :invalid="v$.tckn.$error"
                      :error-messages="tcknMessages"
                    />
                  </div>

                  <div class="mb-0 text-center">
                    <button class="btn btn-primary text-white" type="submit" tabindex="3">
                      <i class="mdi mdi-file"></i>
                      {{ $t('views.pages.view_invoice') }}
                    </button>
                  </div>
                </form>
              </div>
              <!-- end card-body -->
            </div>
            <!-- end card -->
          </div>
          <!-- end col -->
        </div>
        <!-- end row -->
      </div>

      <div class="my-5" v-else>
        <order-items v-if="$country.is('ro')" :order="order" />

        <div v-if="invoice_pdf" class="my-1">
          <div class="row mb-2">
            <div class="col-12 m-auto text-center">
              <base-button @click="downloadInvoice" item-class="btn-info text-white mb-2">
                <i class="mdi mdi-download me-2"></i>
                {{ $t('views.pages.invoice') }} -
                {{ $t('views.pages.download') }}
              </base-button>
            </div>
          </div>
        </div>
        <div v-else-if="receipt_pdf" class="my-1">
          <div>
            <div class="alert alert-info d-print-none">
              <i class="mdi mdi-information-outline mdi-14px me-2"></i>
              <i18n-t keypath="views.pages.bill_is_in_creation_step">
                <template v-slot:phone_number>
                  <strong>
                    {{ $t('views.pages.phone_number') }}
                  </strong>
                </template>
              </i18n-t>
            </div>
          </div>
          <div v-if="$country.isNot('ro')" class="row mb-2">
            <div class="col-12 m-auto text-center">
              <base-button @click="downloadOrderReceipt" item-class="btn-indigo text-white mb-2">
                <i class="mdi mdi-download me-2"></i>
                {{ $t('views.pages.order_receipt') }} -
                {{ $t('views.pages.download') }}
              </base-button>
            </div>
          </div>
        </div>
        <div v-if="late_fee_invoice_pdf" class="my-1">
          <div class="row mb-2">
            <div class="col-12 m-auto text-center">
              <base-button @click="downloadLateFeeInvoice" item-class="btn-danger text-white mb-2">
                <i class="mdi mdi-download me-2"></i>
                {{ $t('views.pages.late_fee_invoice') }} -
                {{ $t('views.pages.download') }}
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- end page -->
</template>

<script>
import { mapState } from 'vuex'

import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import Nprogress from 'nprogress'

import store from '@/store/store.js'

import { errorMessageWithParams } from '@/utilities/validationErrorMessage.js'

import BaseButton from '@/components/BaseButton.vue'
import BaseInput from '@/components/BaseInput.vue'

import BaseLogo from '@/components/BaseLogo.vue'

import OrderItems from './components/orderItems.vue'

export default {
  data() {
    return {
      v$: useVuelidate(),
      tckn: ''
    }
  },
  components: {
    OrderItems,
    BaseLogo,
    BaseInput,
    BaseButton
  },
  validations() {
    return {
      tckn: { required, $lazy: true }
    }
  },
  computed: {
    ...mapState('farmerInvoice', ['order', 'invoice_pdf', 'late_fee_invoice_pdf', 'receipt_pdf']),
    tcknMessages() {
      return errorMessageWithParams(this.v$.tckn, 'views.pages.identification_number')
    }
  },
  methods: {
    status(validation) {
      return {
        error: validation.$error,
        dirty: validation.$dirty
      }
    },
    async getOrder() {
      this.v$.$validate()
      if (!this.v$.$invalid) {
        try {
          Nprogress.start()
          await store.dispatch('farmerInvoice/getOrder', {
            orderId: this.$route.params.id,
            tckn: this.tckn
          })

          if (this.order.logo_invoice) {
            await store.dispatch('farmerInvoice/getInvoice', {
              orderId: this.$route.params.id,
              tckn: this.tckn
            })
          } else {
            await store.dispatch('farmerInvoice/getOrderReceipt', {
              orderId: this.$route.params.id,
              tckn: this.tckn
            })
          }

          if (this.order.late_fee_invoices) {
            await store.dispatch('farmerInvoice/getLateFeeInvoice', {
              orderId: this.$route.params.id,
              tckn: this.tckn
            })
          }

          Nprogress.done()
        } catch (error) {
          Nprogress.done()

          if (error.response?.status === 401) {
            const notification = {
              type: 'danger',
              message: this.$t('views.pages.unauthorized_access')
            }
            store.dispatch('notification/add', notification, { root: true })
          }
        }
      }
    },
    downloadInvoice() {
      let link = document.createElement('a')
      link.href = this.invoice_pdf
      link.download = `${this.$route.params.id}_invoice.pdf`
      link.click()
    },
    downloadLateFeeInvoice() {
      let link = document.createElement('a')
      link.href = this.late_fee_invoice_pdf
      link.download = `${this.$route.params.id}_late_fee_invoice.pdf`
      link.click()
    },
    downloadOrderReceipt() {
      let link = document.createElement('a')
      link.href = this.receipt_pdf
      link.download = `${this.$route.params.id}_order_receipt.pdf`
      link.click()
    }
  }
}
</script>

<style scoped>
input[type='email']:focus,
input[type='password']:focus {
  box-shadow: 0 0 5px #727cf5;
  border: 1px solid #727cf5;
}
.card-header {
  border-bottom: 0px !important;
}
</style>
